import React, { useState } from 'react';
import { ArrowRightIcon, BellIcon, CheckIcon, ClockIcon, MapPinIcon, UsersIcon } from 'lucide-react';
import { activities, type Activity } from '../../data/loisirs';
const channels = ['Notification', 'Text message', 'Email'];
function AlertOptIn({
  label,
  confirmation
}: {
  label: string;
  confirmation: string;
}) {
  const [armed, setArmed] = useState(false);
  const [channel, setChannel] = useState(channels[0]);
  if (!armed) {
    return <button type="button" onClick={() => setArmed(true)} className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-full border border-ville-indigo px-5 py-2.5 text-sm font-bold text-ville-indigo transition-colors duration-150 ease-ville hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1390" data-mp="pv42hnrf7o7p">
        <BellIcon className="h-4 w-4" aria-hidden="true" data-id="element-1391" data-mp="75nz8socphnl" />
        {label}
      </button>;
  }
  return <div className="mt-auto rounded-2xl bg-ville-indigo-soft px-4 py-3" data-id="element-1392" data-mp="274bqnu3zxt4">
      <p className="flex items-start gap-2 text-sm font-semibold text-ville-indigo" data-id="element-1393" data-mp="2hfb2bnsz4cme">
        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1394" data-mp="2mdmh8cy4ldbh" />
        {confirmation}
      </p>
      <div className="mt-2.5 flex flex-wrap gap-1.5" data-id="element-1395" data-mp="2r42tvuu4guws">
        {channels.map(option => <button key={option} type="button" aria-pressed={channel === option} onClick={() => setChannel(option)} className={`rounded-full px-3 py-1 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${channel === option ? 'bg-ville-indigo text-white' : 'bg-white text-ville-navy'}`} data-id="element-1396" data-mp="2w3pgguvubni9">
            {option}
          </button>)}
      </div>
    </div>;
}
function ActivityCard({
  activity,
  onRegister
}: {
  activity: Activity;
  onRegister: (activity: Activity) => void;
}) {
  return <article className="flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-1397" data-mp="3jsqhhx2cfwdi">
      <img src={activity.image} alt="" className="h-40 w-full object-cover" data-id="element-1398" data-mp="3ntaft3ynl618" />
      <div className="flex flex-1 flex-col p-5" data-id="element-1399" data-mp="1op2c0l1p0zot">
        <div className="flex items-start justify-between gap-3" data-id="element-1400" data-mp="1ory9675pvkc7">
          <h3 className="text-[17px] font-extrabold leading-tight text-ville-navy" data-id="element-1401" data-mp="1jqikm54byvp2">{activity.name}</h3>
          {activity.state === 'complet' && <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600" data-id="element-1402" data-mp="1srp6ktpr0ypp">
              Full
            </span>}
        </div>

        <ul className="mt-3 space-y-1.5 text-sm text-slate-600" data-id="element-1403" data-mp="152nr4mii0nax">
          <li className="flex items-center gap-2" data-id="element-1404" data-mp="111bz6ac5jpzk">
            <UsersIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1405" data-mp="10yg2sqfyd9d8" />
            {activity.audience}
          </li>
          <li className="flex items-center gap-2" data-id="element-1406" data-mp="1a1h48dtvicak">
            <MapPinIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1407" data-mp="19zwc0uv175fv" />
            {activity.place}
          </li>
          <li className="flex items-center gap-2" data-id="element-1408" data-mp="hcjoahd4wrkz">
            <ClockIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1409" data-mp="hbs21x5fg67d" />
            {activity.schedule}
          </li>
        </ul>

        <div className="mt-4 flex items-baseline justify-between gap-3" data-id="element-1410" data-mp="2xqlr53tph4lb">
          <p className="text-lg font-extrabold text-ville-navy" data-id="element-1411" data-mp="2xsq40b9l1dxq">{activity.price}</p>
          <p className="text-xs font-semibold text-slate-500" data-id="element-1412" data-mp="2k0s2omm44mc4">{activity.availability}</p>
        </div>

        {activity.state === 'a-venir' && activity.opensOn && <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold text-ville-navy" data-id="element-1413" data-mp="3cnctnlyoewf5">
            Registration opens {activity.opensOn}
          </p>}

        <div className="mt-5 flex flex-1 flex-col justify-end gap-2" data-id="element-1414" data-mp="3gn54k5bjleno">
          {activity.state === 'ouvert' && <>
              <button type="button" onClick={() => onRegister(activity)} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1415" data-mp="37ojdtmvx2tbt">
                Register
              </button>
              <button type="button" className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1416" data-mp="26saywji3cufa">
                View the activity
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1417" data-mp="1xq1qbz6dvb4j" />
              </button>
            </>}

          {activity.state === 'a-venir' && <AlertOptIn label="Notify me when it opens" confirmation="ATNA will let you know as soon as registration opens." data-id="element-1418" data-mp="2qgady6jounqi" />}

          {activity.state === 'complet' && <>
              <button type="button" className="inline-flex w-full items-center justify-center rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1419" data-mp="2gimg37qsd044">
                Put me on the waiting list
              </button>
              <AlertOptIn label="Notify me if a spot opens up" confirmation="ATNA is watching for spots and will let you know." data-id="element-1420" data-mp="zy3rnzrma69w" />
              <p className="text-center text-xs text-slate-400" data-id="element-1421" data-mp="14wf1wbltqisn">Automatic registration options coming soon</p>
            </>}
        </div>
      </div>
    </article>;
}
interface ForYouProps {
  onRegister: (activity: Activity) => void;
}
export function ForYou({
  onRegister
}: ForYouProps) {
  return <section aria-labelledby="recreation-for-you-title" className="mt-14" data-id="element-1422" data-mp="1io1w85w7hh8s">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1423" data-mp="1no7kdc39olm4">
        <div data-id="element-1424" data-mp="1nlv73c8gezfe">
          <h2 id="recreation-for-you-title" className="text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-1425" data-mp="1sn0wp16ti6kp">
            For you
          </h2>
          <p className="mt-1 text-[15px] text-slate-600" data-id="element-1426" data-mp="3pgma2xc1yud3">Activities picked for your profile and your family.</p>
        </div>
        <button type="button" className="text-sm font-bold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1427" data-mp="3uenq19e6mdkc">
          See all activities
        </button>
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1428" data-mp="6hy4zkg5rvh">
        {activities.map(activity => <ActivityCard key={activity.id} activity={activity} onRegister={onRegister} data-id="element-1429" data-mp="3rcsskcuybudu" />)}
      </div>
    </section>;
}